/* eslint-disable no-await-in-loop */
/* eslint-disable no-restricted-syntax */
import {
  useContext,
  useEffect,
  useReducer,
  useCallback,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  propertiesV3 as propertiesV3Api,
  farm,
  propertiesV4 as propertiesV4Api
} from 'utilities/api';

import { Context } from 'components/Store';
import catchCancel from 'helpers/catchCancel';
import sortTree from 'helpers/sortTree';
import { DUMMY_SEASON, FETCH_TYPES } from 'helpers/constants';
import axios from 'axios';
import { parseServerError } from 'helpers/errorHelpers';
import {
  SET_FILTERED_GEOJSON,
  SET_LOAD_COSTS,
  SET_LOAD_PROPERTIES
} from 'reducers/reducer';
import { useFlags } from 'launchdarkly-react-client-sdk';
import createAction from 'helpers/createAction';
import {
  propertyDataReducer,
  resetPropertyData,
  PROPERTY_DATA_INIT_RELOAD,
  PROPERTY_DATA_LOAD_PROPERTIES,
  PROPERTY_DATA_UPDATE_CROP_ZONE_TAGS,
  PROPERTY_DATA_UPDATE_TAG_DEFINITIONS,
  PROPERTY_DATA_SET_FIELDS_LOADED,
  PROPERTY_DATA_SET_GEOJSON_COLLECTION_FEATURES,
  PROPERTY_DATA_CALCULATE_CENTROID,
  PROPERTY_DATA_RESET_MAP_VIEW,
  PROPERTY_DATA_SET_FIELDS,
  PROPERTY_DATA_SET_MAP_CENTROID,
  PROPERTY_DATA_SET_ZOOM,
  SET_PROPERTY_COSTS,
  PROPERTY_PAGINATED_DATA_INIT_RELOAD,
  PROPERTY_DATA_PAGINATED_LOAD_PROPERTIES,
  RESET_PRELOADED_PAGES
} from '../screens/Property/helpers/propertyDataReducer';
import usePropertyFilters from './usePropertyFilters';

// Hoisted so the default is one shared object rather than a fresh one built on
// every call.
const DEFAULT_SEARCH_PARAMS = { page: 0, textSearch: '', orgId: null };
const DEFAULT_ARCHIVED_PARAMS = { includeArchived: true };

const usePopulateMapData = ({
  fetchType,
  cropSeasonsId,
  isCacheOn = false,
  showDanglingFields = false,
  includeArchived = false
}) => {
  const { configureLargeGrowerConditionals } = useFlags();

  // eslint-disable-next-line no-use-before-define
  const dataToBeSorted = useRef();
  const [{ organization, tagChanges }, dispatch] = useContext(Context);
  const [successfulPropertiesLoadId, setSuccessfulPropertiesLoadId] = useState(
    0
  );
  const [lastSuccessfulSeasonIds, setLastSuccessfulSeasonIds] = useState(null);
  const [lastSuccessfulProperties, setLastSuccessfulProperties] = useState(
    null
  );
  const [
    {
      data,
      beforeSearch,
      zoom,
      fieldsAndAreasCentroidCoordinates,
      fieldsAndAreasGeoJSONCollection,
      loading,
      fieldsLoading,
      costs,
      isLoadingPaginatedProperties
    },
    propertyDispatch
  ] = useReducer(propertyDataReducer, resetPropertyData(), resetPropertyData);
  const { runMapFilter } = usePropertyFilters();

  useEffect(() => {
    if (!tagChanges || tagChanges.organizationId !== organization?.id) return;
    propertyDispatch({
      type: PROPERTY_DATA_UPDATE_TAG_DEFINITIONS,
      payload: tagChanges
    });
  }, [tagChanges, organization?.id]);

  const dataRef = useRef(data);
  dataRef.current = data;

  const beforeSearchRef = useRef(beforeSearch);
  beforeSearchRef.current = beforeSearch;

  const filteredGeoJSON = useMemo(
    () =>
      includeArchived
        ? fieldsAndAreasGeoJSONCollection
        : runMapFilter(fieldsAndAreasGeoJSONCollection),
    [fieldsAndAreasGeoJSONCollection, runMapFilter, includeArchived]
  );

  const handleError = useCallback(
    err => {
      if (axios.isCancel(err)) {
        catchCancel(err);
      } else {
        parseServerError(dispatch)(err);
      }
    },
    [dispatch]
  );

  const setGeoJSONCollectionFeatures = useCallback((value, isEdition) => {
    return propertyDispatch({
      type: PROPERTY_DATA_SET_GEOJSON_COLLECTION_FEATURES,
      payload: !isEdition
        ? value
        : {
            isEdition,
            value
          }
    });
  }, []);

  const initialSettings = useCallback(
    (body, isPaginated = false, hideLoader = false) => {
      let seasonIds = JSON.parse(localStorage.getItem('selectedCropSeasons'));
      if (seasonIds === null) {
        seasonIds = [DUMMY_SEASON];
      }
      const v3PropertiesPostBody = {
        seasonIds
      };

      const postBody = body || v3PropertiesPostBody || {};
      if (!postBody?.seasonIds?.length) {
        postBody.seasonIds = [DUMMY_SEASON];
      }

      propertyDispatch({
        type: isPaginated
          ? PROPERTY_PAGINATED_DATA_INIT_RELOAD
          : PROPERTY_DATA_INIT_RELOAD
      });

      if (!hideLoader) dispatch({ type: SET_LOAD_PROPERTIES, payload: true });

      return postBody;
    },
    [dispatch]
  );

  const isOrgChange = (params, _data) =>
    !!params.orgId && _data.orgId !== params.orgId;

  const isOverPageLimit = (params, pagination) =>
    (params.page === 0 && pagination?.totalPage === 1) ||
    params.page >= pagination?.totalPages;

  const isPreloadedPage = (params, pagination) => {
    return pagination?.preLoadedPages?.includes(params.page);
  };

  const handlePreloadedOrOverPage = (params, pagination, _data) => {
    propertyDispatch({
      type: PROPERTY_DATA_PAGINATED_LOAD_PROPERTIES,
      payload: {
        properties: [],
        orgId: _data.orgId,
        referencePage: pagination.referencePage,
        parentPage: params.page,
        totalPages: pagination.totalPages
      }
    });
    propertyDispatch({ type: PROPERTY_DATA_SET_FIELDS_LOADED });
  };

  const fetchPaginatedMapProperties = useCallback(
    async (body, params = DEFAULT_SEARCH_PARAMS) => {
      const pageSize = params?.textSearch
        ? 200
        : configureLargeGrowerConditionals.pageSize;
      const currentData = dataRef.current;
      const orgChange = isOrgChange(params, currentData);

      if (orgChange) {
        propertyDispatch({ type: RESET_PRELOADED_PAGES });
      } else if (
        params.textSearch &&
        isOverPageLimit(params, currentData?.pagination) &&
        !!currentData.properties?.length
      ) {
        return;
      } else if (!params.textSearch) {
        const pagination =
          beforeSearchRef.current?.data?.pagination || currentData.pagination;
        if (
          isPreloadedPage(params, pagination) ||
          isOverPageLimit(params, pagination)
        ) {
          handlePreloadedOrOverPage(params, pagination, currentData);
          return;
        }
      }

      try {
        const postBody = initialSettings(body, true, !params.textSearch);
        const response = await propertiesV4Api.fetchByPost(
          {
            ...postBody,
            include_extended: true
          },
          {
            pageNo: params.page,
            size: pageSize,
            ...(params.textSearch ? { name: params.textSearch } : {})
          },
          isCacheOn,
          showDanglingFields
        ).promise;
        setSuccessfulPropertiesLoadId(id => id + 1);
        setLastSuccessfulSeasonIds(postBody.seasonIds ?? []);
        setLastSuccessfulProperties(response?.data?.data?.properties ?? []);

        if (response?.data?.data?.properties?.length || !!params?.textSearch) {
          propertyDispatch({
            type: PROPERTY_DATA_PAGINATED_LOAD_PROPERTIES,
            payload: {
              ...response.data.data,
              referencePage: params.page,
              parentPage: params.page,
              totalPages: response.data.totalPages,
              textSearch: params.textSearch
            }
          });
        }
      } catch (err) {
        handleError(err);
      } finally {
        propertyDispatch({ type: PROPERTY_DATA_SET_FIELDS_LOADED });
      }
    },
    [
      configureLargeGrowerConditionals,
      handleError,
      initialSettings,
      isCacheOn,
      showDanglingFields,
      propertyDispatch
    ]
  );

  const fetchMapProperties = useCallback(
    async (body, params = DEFAULT_ARCHIVED_PARAMS) => {
      try {
        const { includeArchived: includeArchivedParam } = params;
        const postBody = initialSettings(body);
        const response = await propertiesV3Api.fetchByPost(
          {
            ...postBody,
            include_extended: true
          },
          {
            includeArchived: includeArchivedParam
          },
          isCacheOn,
          showDanglingFields
        ).promise;
        setSuccessfulPropertiesLoadId(id => id + 1);
        setLastSuccessfulSeasonIds(postBody.seasonIds ?? []);
        setLastSuccessfulProperties(response?.data?.properties ?? []);
        if (response?.data?.properties?.length) {
          propertyDispatch({
            type: PROPERTY_DATA_LOAD_PROPERTIES,
            payload: response.data
          });
        }
      } catch (err) {
        handleError(err);
      } finally {
        propertyDispatch({ type: PROPERTY_DATA_SET_FIELDS_LOADED });
        dispatch({ type: SET_LOAD_PROPERTIES, payload: false });
      }
    },
    [dispatch, handleError, initialSettings, isCacheOn, showDanglingFields]
  );

  const fetchCropSeasonsMapData = useCallback(
    async body => {
      try {
        const seasonIds = [cropSeasonsId].flat();
        const v3PropertiesPostBody = {
          seasonIds
        };

        const postBody = body || v3PropertiesPostBody || {};

        propertyDispatch({ type: PROPERTY_DATA_INIT_RELOAD });
        dispatch({ type: SET_LOAD_PROPERTIES, payload: true });

        const response = await propertiesV3Api.fetchByPost(
          {
            ...postBody,
            include_extended: true
          },
          includeArchived ? { includeArchived: true } : null,
          isCacheOn,
          showDanglingFields
        ).promise;
        setSuccessfulPropertiesLoadId(id => id + 1);
        setLastSuccessfulSeasonIds(postBody.seasonIds ?? []);
        setLastSuccessfulProperties(response?.data?.properties ?? []);

        propertyDispatch({
          type: PROPERTY_DATA_LOAD_PROPERTIES,
          payload: response.data
        });
      } catch (err) {
        handleError(err);
      } finally {
        propertyDispatch({ type: PROPERTY_DATA_SET_FIELDS_LOADED });
        dispatch({ type: SET_LOAD_PROPERTIES, payload: false });
      }
    },
    [
      cropSeasonsId,
      dispatch,
      handleError,
      isCacheOn,
      includeArchived,
      showDanglingFields
    ]
  );

  const fetchFarmCosts = useCallback(async (farmId, body) => {
    const costsApi = farm.createChildApi({
      action: `farm/${farmId}/cost`
    });

    try {
      const seasonIds = JSON.parse(localStorage.getItem('selectedCropSeasons'));

      const v3PropertiesPostBody = {
        seasonIds
      };

      const postBody = body || v3PropertiesPostBody || {};

      dispatch({ type: SET_LOAD_COSTS, payload: true });

      if (
        !(postBody.seasonIds?.length > 0) ||
        new Set(postBody.seasonIds).has(DUMMY_SEASON)
      ) {
        propertyDispatch({
          type: SET_PROPERTY_COSTS,
          payload: {
            costs: {},
            farmId
          }
        });

        return;
      }

      const response = await costsApi.post(postBody).promise;

      propertyDispatch({
        type: SET_PROPERTY_COSTS,
        payload: {
          costs: response.data,
          farmId
        }
      });
    } catch (err) {
      if (
        err?.response?.data?.displayErrorMessage ===
        'Could not retrieve any task with the specified filters'
      ) {
        return;
      }
      if (
        err?.response?.data?.displayErrorMessage ===
        'Could not convert from CWF season to Base season'
      ) {
        return;
      }
      handleError(err);
    } finally {
      dispatch({ type: SET_LOAD_COSTS, payload: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reloadData = useCallback(
    (body, params) => {
      switch (fetchType) {
        case FETCH_TYPES.propertiesMap:
          return fetchMapProperties(body, params);
        case FETCH_TYPES.paginatedPropertiesMap:
          return fetchPaginatedMapProperties(body, params);
        case FETCH_TYPES.cropSeasonsMap:
          return fetchCropSeasonsMapData(body);
        default:
          throw new Error(fetchType);
      }
    },
    [
      fetchMapProperties,
      fetchPaginatedMapProperties,
      fetchCropSeasonsMapData,
      fetchType
    ]
  );

  // Get or compute the centroid value
  useEffect(() => {
    if (!loading) {
      return;
    }

    if (data && fieldsAndAreasGeoJSONCollection) {
      if (fieldsAndAreasGeoJSONCollection.features.length > 0) {
        propertyDispatch({
          type: PROPERTY_DATA_CALCULATE_CENTROID
        });
      } else {
        propertyDispatch({
          type: PROPERTY_DATA_RESET_MAP_VIEW
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, fieldsAndAreasGeoJSONCollection, loading]);

  useMemo(() => {
    dataToBeSorted.current = sortTree(data);
  }, [data]);

  const dataSorted = dataToBeSorted.current;

  const setFields = useCallback(
    value =>
      propertyDispatch({
        type: PROPERTY_DATA_SET_FIELDS,
        payload: value
      }),
    []
  );
  const updateCropZoneTags = (cropZoneId, tags) => {
    propertyDispatch({
      type: PROPERTY_DATA_UPDATE_CROP_ZONE_TAGS,
      payload: { cropZoneId, tags }
    });
  };
  const setFieldsAndAreasCentroidCoordinates = useCallback(
    value =>
      propertyDispatch({
        type: PROPERTY_DATA_SET_MAP_CENTROID,
        payload: value
      }),
    []
  );
  const setZoom = useCallback(value => {
    return propertyDispatch({
      type: PROPERTY_DATA_SET_ZOOM,
      payload: value
    });
  }, []);

  const getSsurgoInfo = useCallback(() => {
    return Promise.resolve({
      slope: 3,
      soilTexture: 'loam',
      slopeLength: 13,
      hydrologyGroup: 'C'
    });
  }, []);

  const filterFeatureCollectionByParentId = useCallback(
    (fc, parentId, parents = []) =>
      fc.features
        ? fc.features.map(feat => {
            const layer = parentId || parents.length ? 'default' : 'focused';
            const newLayer =
              parentId === feat.properties?.id ||
              (parentId === feat.properties?.propertyId &&
                !feat.properties?.$parentDuplicated) ||
              parents.some(parent => parent.id === feat.properties?.id)
                ? 'focused'
                : layer;
            if (feat.properties?.$layer === newLayer) return feat;
            return {
              ...feat,
              properties: { ...feat.properties, $layer: newLayer }
            };
          })
        : [],
    []
  );

  useEffect(() => {
    if (filteredGeoJSON?.features?.length) {
      createAction(dispatch, SET_FILTERED_GEOJSON, filteredGeoJSON);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredGeoJSON]);

  const returnValue = useMemo(
    () => ({
      pagination: data?.pagination,
      fieldsAndAreasCentroidCoordinates,
      fieldsAndAreasGeoJSONCollection: filteredGeoJSON,
      setGeoJSONCollectionFeatures,
      getSsurgoInfo,
      loading,
      reloadData,
      setFields,
      updateCropZoneTags,
      setFieldsAndAreasCentroidCoordinates,
      setZoom,
      zoom,
      dataSorted,
      costs,
      fieldsLoading,
      filterFeatureCollectionByParentId,
      fetchFarmCosts,
      isLoadingPaginatedProperties,
      successfulPropertiesLoadId,
      lastSuccessfulSeasonIds,
      lastSuccessfulProperties
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      data?.pagination,
      fieldsAndAreasCentroidCoordinates,
      filteredGeoJSON,
      loading,
      reloadData,
      zoom,
      dataSorted,
      costs,
      fieldsLoading,
      isLoadingPaginatedProperties,
      successfulPropertiesLoadId,
      lastSuccessfulSeasonIds,
      lastSuccessfulProperties
    ]
  );

  return returnValue;
};

export default usePopulateMapData;
